'use client'

import { useEffect } from 'react'
import { GA_ENABLED, gaEvent } from '@/lib/gtag'
import { BOOK_A_DEMO_HREF } from '@/lib/marketing-links'

// Acquisition events for the public marketing shell (see the GA4 measurement
// plan, §6). Renders nothing.
//
// Everything here is a DELEGATED listener on `document` rather than a prop on
// the section that owns the element. That is deliberate: the landing sections
// are server components rendering markup ported verbatim from the design
// artifact, and adding an onClick to each would turn four of them into client
// components — shipping the landing page's JS bundle to every visitor to
// measure it. One listener costs nothing and leaves the sections untouched.
//
// `placement` comes from the nearest `data-slot`, which the design-system rules
// require on every component root — so a CTA moving between sections relabels
// itself, and a new section is self-describing with no change here.

/** Demo CTA host, derived from the link itself so the two cannot drift. */
const DEMO_HOST = new URL(BOOK_A_DEMO_HREF).host

const SCROLL_MILESTONES = [25, 50, 75, 90] as const

function placementOf(el: Element | null): string {
  const slotted = el?.closest('[data-slot]')
  return slotted?.getAttribute('data-slot') ?? 'unknown'
}

/** `/articles/<slug>/<file>` -> `<slug>`, so the event says which piece the
    download came from without the caller reading the route. */
function articleSlug(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean)
  return parts[0] === 'articles' && parts.length > 1 ? parts[1]! : 'unknown'
}

export function MarketingEvents() {
  useEffect(() => {
    if (!GA_ENABLED) return

    // --- Demo CTA -------------------------------------------------------
    // Capture phase: the click must be recorded even if something downstream
    // calls stopPropagation, and this listener never prevents the navigation.
    const onClick = (e: MouseEvent) => {
      const anchor = (e.target as Element | null)?.closest?.('a')
      if (!(anchor instanceof HTMLAnchorElement)) return
      // An article's give-away. `.skill` is not in GA4's default file-download
      // list and these links carry no CTA host, so without this the article's
      // entire close — three skills, free, at the end — produced no signal at
      // all: the one conversion the piece exists to drive was unmeasurable.
      const file = anchor.getAttribute('data-article-download')
      if (file) {
        gaEvent('article_download', { file, article: articleSlug(anchor.pathname) })
        return
      }
      if (anchor.host !== DEMO_HOST) return
      gaEvent('demo_click', { placement: placementOf(anchor) })
    }
    document.addEventListener('click', onClick, true)

    // --- FAQ ------------------------------------------------------------
    // `toggle` does not bubble, but a capture-phase listener on an ancestor
    // still sees it on the way down.
    const onToggle = (e: Event) => {
      const details = e.target
      if (!(details instanceof HTMLDetailsElement) || !details.open) return
      const question = details.querySelector('summary')?.textContent?.trim()
      gaEvent('faq_open', { question: question?.slice(0, 100) ?? 'unknown' })
    }
    document.addEventListener('toggle', onToggle, true)

    // --- Scroll depth ---------------------------------------------------
    // Read-only against layout, coalesced into one rAF per frame, and each
    // milestone fires at most once per page.
    const reached = new Set<number>()
    let queued = false
    const measure = () => {
      queued = false
      const scrollable = document.documentElement.scrollHeight - window.innerHeight
      if (scrollable <= 0) return
      const percent =
        ((window.scrollY + window.innerHeight) / (scrollable + window.innerHeight)) * 100
      for (const milestone of SCROLL_MILESTONES) {
        if (percent >= milestone && !reached.has(milestone)) {
          reached.add(milestone)
          gaEvent('scroll_milestone', { percent: milestone })
        }
      }
    }
    const onScroll = () => {
      if (queued) return
      queued = true
      requestAnimationFrame(measure)
    }
    window.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      document.removeEventListener('click', onClick, true)
      document.removeEventListener('toggle', onToggle, true)
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  return null
}
